/**
 * DataGrid Component
 * 
 * A presentational component for displaying tabular data using Engine column metadata.
 * Works exclusively with MaterializedTableData from the Engine.
 * 
 * Design decisions:
 * - Only supports single-column sorting (replaces the entire orderBy array when sorting)
 * - Sort indicators reflect the state of the last run query, not local state
 * - Columns are derived from the result data schema to ensure consistency
 */

import * as React from 'react';
import { DataGridProps, Column, ColumnRef } from './types';
import { LogicalType, SelectQueryOptions, MaterializedTableData, SortDirectionEnum } from '../../../lib/engine';
import { cn, ScrollArea, ScrollBar } from '../../../components/ui';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { useDataGrid } from './use-data-grid';
import { getCellValue } from './data-access';
import { formatCellValue, safeToString } from './cell-formatting';
import { calculateRowRange, RowRange } from './pagination';

// Define GridChildComponentProps interface to replace the one from react-window
interface GridChildComponentProps {
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
}

// Helper function to create columns from MaterializedTableData schema
function createColumnsFromSchema(data: MaterializedTableData | null): Column[] {
  if (!data?.table?.schema?.fields) {
    return [];
  }

  return data.table.schema.fields.map(field => ({
    name: field.name,
    header: field.name,
    type: field.type?.toString() || 'string',
    nullable: true,
    sortable: true,
    resizable: true,
    filterable: true,
    width: 150
  }));
}

// Memoized cell component to prevent unnecessary re-renders
const MemoizedCell = React.memo(
  ({ rowIndex, columnIndex, column, data, style, onClick }: {
    rowIndex: number;
    columnIndex: number;
    column: Column;
    data: MaterializedTableData;
    style?: React.CSSProperties;
    onClick?: () => void;
  }) => {
    const value = getCellValue({ 
      rowIndex, 
      columnName: column.name, 
      data 
    });
    const formattedValue = formatCellValue({ value, column });
    
    return (
      <td
        className={cn(
          "px-3 py-1.5 border-b",
          "text-sm",
          column.type === 'number' ? "text-right" : "",
          "border-border",
          "whitespace-nowrap overflow-hidden text-ellipsis"
        )}
        style={style}
        onClick={onClick}
      >
        {formattedValue}
      </td>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if row index, column, or data changes
    // This prevents re-renders when only column widths change
    return (
      prevProps.rowIndex === nextProps.rowIndex &&
      prevProps.columnIndex === nextProps.columnIndex &&
      prevProps.column === nextProps.column &&
      prevProps.data === nextProps.data
    );
  }
);

// Add display name
MemoizedCell.displayName = 'MemoizedCell';

// Memoized row component to prevent unnecessary re-renders
const MemoizedRow = React.memo(
  ({ 
    rowIndex, 
    columns, 
    data, 
    onRowClick, 
    onCellClick 
  }: { 
    rowIndex: number; 
    columns: Column[]; 
    data: any; 
    onRowClick?: (rowIndex: number) => void;
    onCellClick?: (rowIndex: number, column: Column) => void;
  }) => {
    return (
      <tr
        className={cn(
          "hover:bg-muted/50",
          onRowClick && "cursor-pointer"
        )}
        onClick={() => onRowClick && onRowClick(rowIndex)}
      >
        {columns.map((column) => (
          <MemoizedCell
            key={column.name}
            rowIndex={rowIndex}
            columnIndex={columns.indexOf(column)}
            column={column}
            data={data}
            onClick={() => onCellClick && onCellClick(rowIndex, column)}
          />
        ))}
      </tr>
    );
  }
);

// Add display name
MemoizedRow.displayName = 'MemoizedRow';

/**
 * Create a column reference from a column name and available columns
 */
function createColumnRef({ name, columns }: { name: string; columns: Column[] }): ColumnRef | undefined {
  const column = columns.find(col => col.name === name);
  if (!column) return undefined;
  
  return {
    name,
    schema: column
  };
}

/**
 * DataGrid Component
 */
export function DataGrid(props: DataGridProps) {
  const {
    columns: providedColumns, // Rename to make it clear these are provided columns
    data,
    loading = false,
    error = null,
    height = '100%',
    width = '100%',
    className,
    style,
    // Pagination props
    page = 1,
    pageSize = 25,
    totalRows,
    onPageChange,
    onPageSizeChange,
    // Click handlers
    onRowClick,
    onCellClick,
    // Query props
    query,
    updateQuery,
    lastExecutedQuery,
    executeQuery,
  } = props;

  // Create a ref for the scroll container to sync header and body scrolling
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Derive columns from the result data schema
  const resultColumns = React.useMemo(() => {
    return createColumnsFromSchema(data);
  }, [data]);

  // Use the DataGrid hook to manage column widths first
  const { columnWidths, isResizing, resizingColumn, actions } = useDataGrid({
    ...props,
    columns: resultColumns // Use result columns initially
  });
  const { handleColumnResizeStart, getColumnWidth } = actions;

  // Merge provided column properties (like width) with result columns
  const columnsWithWidths = React.useMemo(() => {
    if (!resultColumns || resultColumns.length === 0) {
      return [];
    }
    
    return resultColumns.map(resultColumn => {
      const providedColumn = providedColumns.find(c => c.name === resultColumn.name);
      return {
        ...resultColumn,
        width: providedColumn?.width || getColumnWidth(resultColumn)
      };
    });
  }, [resultColumns, providedColumns, getColumnWidth]);

  // Internal state for sorting - this will be driven by the query prop
  const [sortInfo, setSortInfo] = React.useState<{
    field: string | null;
    direction: 'asc' | 'desc' | null;
  }>({ field: null, direction: null });

  // Internal pagination state if not controlled externally
  const [internalPage, setInternalPage] = React.useState(page);
  const [internalPageSize, setInternalPageSize] = React.useState(pageSize);

  // Reference to track if we need to preserve column widths
  const prevPageRef = React.useRef(page);

  // Add logging to see what columns and data the DataGrid receives
  React.useEffect(() => {
    if (columnsWithWidths.length === 0) {
      console.warn('DataGrid received empty columns array');
    }
    
    if (data && data.table && data.table.schema && data.table.schema.fields && 
        columnsWithWidths.length > 0 && data.table.schema.fields.length !== columnsWithWidths.length) {
      console.warn('Column count mismatch:', {
        columnsLength: columnsWithWidths.length,
        schemaFieldsLength: data.table.schema.fields.length,
        columns: columnsWithWidths.map(c => c.name),
        schemaFields: data.table.schema.fields.map((f: any) => f.name)
      });
    }
  }, [columnsWithWidths, data]);

  // Add debugging logs
  React.useEffect(() => {
  }, [columnsWithWidths, data, loading, error]);

  // Extract sort information from the query if available
  // This is the key effect that ensures the sort indicators reflect the last run query
  React.useEffect(() => {
    if (!query) {
      return;
    }
    
    // Reset sort info if query type is SQL
    if (query.type === 'SQL') {
      setSortInfo({ field: null, direction: null });
      return;
    }
    
    // Handle both SELECT and AGGREGATE query types
    if (query.type === 'SELECT' || query.type === 'AGGREGATE') {
      const queryOptions = query.options as any; // Using any to handle both types
      
      if (!queryOptions.orderBy || queryOptions.orderBy.length === 0) {
        setSortInfo({ field: null, direction: null });
        return;
      }
      
      // We only support single-column sorting, so we only look at the first orderBy clause
      const firstOrderBy = queryOptions.orderBy[0];
      
      if (firstOrderBy && firstOrderBy.column) {
        const newSortInfo = {
          field: firstOrderBy.column.name,
          direction: firstOrderBy.direction === 'ASC' ? 'asc' as const : 'desc' as const
        };
        setSortInfo(newSortInfo);
      }
    }
  }, [query]);


  // Update internal state when props change
  React.useEffect(() => {
    setInternalPage(page);
  }, [page]);

  React.useEffect(() => {
    setInternalPageSize(pageSize);
  }, [pageSize]);

  // Preserve column widths when changing pages
  React.useEffect(() => {
    prevPageRef.current = page;
  }, [page]);

  // Add mouse event handlers for column resizing
  React.useEffect(() => {
    if (!isResizing || !resizingColumn) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (actions.handleColumnResizing) {
        actions.handleColumnResizing(e.clientX);
      }
    };
    
    const handleMouseUp = () => {
      if (actions.handleColumnResizeEnd) {
        actions.handleColumnResizeEnd();
      }
    };
    
    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Restore default styles
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, resizingColumn, actions]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    } else {
      setInternalPageSize(newSize);
      setInternalPage(1); // Reset to first page when changing page size
    }
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    console.log('DataGrid handleSortChange called for field:', field);
    
    if (!updateQuery || !query) {
      console.warn('DataGrid: Cannot sort - updateQuery or query is missing', { updateQuery: !!updateQuery, query: !!query });
      return;
    }
    
    // Use the lastExecutedQuery if available, otherwise fall back to the current query
    const queryToUpdate = lastExecutedQuery || query;
    console.log('Using query for sort:', queryToUpdate);
    
    // Only allow sorting in SELECT or AGGREGATE query modes
    if (queryToUpdate.type !== 'SELECT' && queryToUpdate.type !== 'AGGREGATE') {
      console.warn('Query type not supported for sorting:', queryToUpdate.type);
      return;
    }
    
    // Get the current sort state from the query, not the local state
    // This ensures we're working with the actual query state that will be executed
    let currentField = null;
    let currentDirection = null;
    
    // Extract current sort info from the query
    if (queryToUpdate.type === 'SELECT' || queryToUpdate.type === 'AGGREGATE') {
      const queryOptions = queryToUpdate.options as any;
      if (queryOptions.orderBy && queryOptions.orderBy.length > 0) {
        // Note: We only support single-column sorting, so we only look at the first orderBy clause
        const firstOrderBy = queryOptions.orderBy[0];
        if (firstOrderBy && firstOrderBy.column) {
          currentField = firstOrderBy.column.name;
          currentDirection = firstOrderBy.direction === 'ASC' ? 'asc' : 'desc';
        }
      }
    }
    
    // Determine the new sort direction
    let newDirection: 'asc' | 'desc' | null = null;
    
    if (currentField === field) {
      // Toggle direction for the same field
      if (currentDirection === 'asc') {
        newDirection = 'desc';
      } else if (currentDirection === 'desc') {
        newDirection = null;
      } else {
        newDirection = 'asc';
      }
    } else {
      // New sort field
      newDirection = 'asc';
    }
    
    
    // Create a new query with the updated sort
    const newQuery = { ...queryToUpdate };
    
    // Update the orderBy clause based on query type
    if (queryToUpdate.type === 'SELECT') {
      const selectOptions = newQuery.options as SelectQueryOptions;
      
      // Update the orderBy clause
      if (newDirection === null) {
        // Remove sorting
        selectOptions.orderBy = [];

      } else {
        // Create column reference
        const columnRef = createColumnRef({ name: field, columns: columnsWithWidths });
        if (!columnRef) {
          console.warn('Could not create column reference for:', field);
          return;
        }
        
        // Create orderBy clause
        const orderByClause = {
          column: columnRef,
          direction: newDirection === 'asc' ? 'ASC' as SortDirectionEnum : 'DESC' as SortDirectionEnum
        };
        
        // Note: We only support single-column sorting, so we replace the entire orderBy array
        selectOptions.orderBy = [orderByClause];

      }
    } else if (queryToUpdate.type === 'AGGREGATE') {
      // For AGGREGATE queries, apply similar logic but with the appropriate options structure
      const aggregateOptions = newQuery.options as any; // Using any for now, should be typed properly
      
      if (newDirection === null) {
        // Remove sorting
        aggregateOptions.orderBy = [];

      } else {
        // Create column reference
        const columnRef = createColumnRef({ name: field, columns: columnsWithWidths });
        if (!columnRef) {
          console.warn('Could not create column reference for:', field);
          return;
        }
        
        // Create orderBy clause
        const orderByClause = {
          column: columnRef,
          direction: newDirection === 'asc' ? 'ASC' as SortDirectionEnum : 'DESC' as SortDirectionEnum
        };
        
        // Note: We only support single-column sorting, so we replace the entire orderBy array
        aggregateOptions.orderBy = [orderByClause];

      }
    }
    
    // Don't update local state - let the query update trigger the useEffect
    // that will update the sortInfo state based on the last executed query
    
    // Update the query and execute it immediately
    updateQuery(newQuery);
    
    // If we have an executeQuery function available, execute the query immediately
    if (executeQuery) {
      executeQuery(newQuery);
    }
    
    console.log('DataGrid handleSortChange completed, new query:', newQuery);
  };

  // Calculate the number of rows to display based on pagination
  const getRowRange = React.useCallback((): RowRange => {
    if (!data || !data.rowCount) {
      return { startRow: 0, endRow: 0, totalPages: 0 };
    }
    
    const currentPage = onPageChange ? page : internalPage;
    const currentPageSize = onPageSizeChange ? pageSize : internalPageSize;
    const actualTotalRows = totalRows || data.rowCount;
    
    return calculateRowRange({
      totalRows: actualTotalRows,
      currentPage,
      pageSize: currentPageSize
    });
  }, [data, page, internalPage, pageSize, internalPageSize, onPageChange, onPageSizeChange, totalRows]);

  // State for delayed loading indicator
  const [showLoadingIndicator, setShowLoadingIndicator] = React.useState(false);
  const loadingTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Effect to handle delayed loading indicator
  React.useEffect(() => {
    // Clear any existing timer
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }

    if (loading) {
      // Set a timer to show the loading indicator after a delay
      loadingTimerRef.current = setTimeout(() => {
        setShowLoadingIndicator(true);
      }, 300); // 300ms delay before showing loading indicator
    } else {
      // Immediately hide the loading indicator when loading is done
      setShowLoadingIndicator(false);
    }

    // Cleanup function
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading]);

  // Render loading state - now as an overlay
  const renderLoading = () => {
    return (
      <div className="absolute inset-0 bg-background/30 backdrop-blur-[0.5px] flex items-center justify-center z-10 transition-opacity duration-150">
        <div className="bg-background/90 px-4 py-2 rounded-lg shadow-md flex items-center space-x-3 border border-border/50">
          <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
          <span className="text-xs font-medium text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  };

  // Render error state
  const renderError = () => {
    // We're now using toast notifications for errors, so we'll just render a more subtle error message
    return (
      <div className="m-4 p-4 border border-destructive/30 bg-destructive/5 text-destructive rounded">
        <h4 className="font-bold">Query Error</h4>
        <p className="text-sm">
          There was an error executing your query. Please check the query syntax and try again.
        </p>
        <p className="text-xs mt-2 text-muted-foreground">
          Error details have been shown in a notification. You can copy the full error message from there.
        </p>
      </div>
    );
  };

  // Render empty state
  const renderEmpty = () => {
    // If we have columns but no data, still render the headers
    if (columnsWithWidths && columnsWithWidths.length > 0) {
      
      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1 overflow-hidden w-full">
            <ScrollArea className="w-full h-full">
              <div style={{ width: '100%' }}>
                <table className="w-full border-collapse">
                  <thead className="bg-background z-10 border-b sticky top-0">
                    <tr>
                      {columnsWithWidths.map((column) => (
                        <th
                          key={`${column.name}-${sortInfo.field === column.name ? sortInfo.direction : 'none'}`}
                          id={column.name}
                          className={cn(
                            "px-3 py-1.5 text-left border-b font-medium text-sm",
                            column.sortable && query?.type !== 'SQL' && "cursor-pointer select-none",
                            sortInfo.field === column.name && "bg-primary/10 border-primary border-b-2 text-primary"
                          )}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth || 30,
                            maxWidth: column.maxWidth,
                            position: 'relative',
                            cursor: (column.sortable && query?.type !== 'SQL') ? 'pointer' : 'default',
                          }}
                          onClick={() => column.sortable && query?.type !== 'SQL' && handleSortChange(column.name)}
                        >
                          <div className="flex items-center gap-1 relative w-full">
                            <span>{column.header || column.name}</span>
                            {column.sortable && query?.type !== 'SQL' && (
                              <div className="ml-1 flex items-center">
                                {sortInfo.field === column.name ? (
                                  <div className="text-primary">
                                    {sortInfo.direction === 'asc' ? (
                                      <ArrowUp className="h-3 w-3" strokeWidth={2} />
                                    ) : (
                                      <ArrowDown className="h-3 w-3" strokeWidth={2} />
                                    )}
                                  </div>
                                ) : (
                                  <ArrowUpDown className="h-2.5 w-2.5 text-muted-foreground opacity-50" />
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Column resize handle */}
                          {column.resizable !== false && (
                            <div
                              key={`resize-${column.name}`}
                              className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize group hover:bg-muted/30"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleColumnResizeStart(column.name, column.width, e.clientX);
                              }}
                            >
                              <div className="h-4/5 w-[1px] mx-auto my-auto bg-primary/50 group-hover:bg-primary" />
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={columnsWithWidths.length} className="text-center p-8 text-muted-foreground">
                        No data available
                      </td>
                    </tr>
                  </tbody>
                </table>
                <ScrollBar orientation="horizontal" />
              </div>
            </ScrollArea>
          </div>
        </div>
      );
    }
    
    // Default empty state if no columns
    return (
      <div className="flex items-center justify-center p-4 h-full w-full text-muted-foreground">
        <p>No data available</p>
      </div>
    );
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    if (!data || !data.rowCount) return null;
    
    const { totalPages } = getRowRange();
    const currentPage = Math.min(onPageChange ? page : internalPage, totalPages);
    
    // If only one page, don't show pagination
    if (totalPages <= 1) return null;
    
    const buttons = [];
    
    // Always show first page
    buttons.push(
      <button
        key="first"
        className={`h-7 w-7 rounded-md flex items-center justify-center text-sm ${currentPage === 1 ? 'border border-input bg-background' : 'hover:bg-accent hover:text-accent-foreground'}`}
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
      >
        1
      </button>
    );
    
    // Show ellipsis if needed
    if (currentPage > 3) {
      buttons.push(
        <div 
          key="ellipsis-1"
          className="flex h-7 w-7 items-center justify-center"
        >
          <span className="h-4 w-4">...</span>
          <span className="sr-only">More pages</span>
        </div>
      );
    }
    
    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      if (i === 1 || i === totalPages) continue; // Skip first and last page as they're always shown
      buttons.push(
        <button
          key={i}
          className={`h-7 w-7 rounded-md flex items-center justify-center text-sm ${currentPage === i ? 'border border-input bg-background' : 'hover:bg-accent hover:text-accent-foreground'}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    
    // Show ellipsis if needed
    if (currentPage < totalPages - 2) {
      buttons.push(
        <div 
          key="ellipsis-2"
          className="flex h-7 w-7 items-center justify-center"
        >
          <span className="h-4 w-4">...</span>
          <span className="sr-only">More pages</span>
        </div>
      );
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      buttons.push(
        <button
          key="last"
          className={`h-7 w-7 rounded-md flex items-center justify-center text-sm ${currentPage === totalPages ? 'border border-input bg-background' : 'hover:bg-accent hover:text-accent-foreground'}`}
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          {totalPages}
        </button>
      );
    }
    
    return (
      <div className="flex items-center justify-center gap-1 py-1.5 border-t">
        {buttons}
      </div>
    );
  };

  // Cell renderer callback - moved outside of renderGrid to avoid conditional hook creation
  // Note: This function is defined for potential use with virtual list rendering libraries,
  // but is not currently used in the main rendering logic. The table rendering is handled
  // by the renderGrid function below which uses a standard table structure.
  const cellRenderer = React.useCallback(
    ({ columnIndex, rowIndex, style }: GridChildComponentProps) => {
      // Header row
      if (rowIndex === 0) {
        const column = columnsWithWidths[columnIndex];
        if (!column) return null;
        
        return (
          <div 
            className={cn(
              "data-grid-header-cell px-3 py-1.5 text-left border-b font-medium text-sm bg-background sticky top-0 z-10",
              column.sortable && query?.type !== 'SQL' && "cursor-pointer select-none"
            )} 
            style={{
              ...style,
              width: column.width || 'auto',
              minWidth: column.minWidth || 30,
              maxWidth: column.maxWidth || 'none',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {column.header || column.name}
          </div>
        );
      }

      // Data rows (adjust rowIndex to account for header row)
      const dataRowIndex = rowIndex - 1;
      const column = columnsWithWidths[columnIndex];
      if (!column) return null;
      
      return (
        <MemoizedCell
          rowIndex={dataRowIndex}
          columnIndex={columnIndex}
          column={column}
          data={data}
          style={{
            width: column.width || 'auto',
            minWidth: column.minWidth || 30,
            maxWidth: column.maxWidth || 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '0.375rem 0.75rem'
          }}
          onClick={() => onCellClick && onCellClick(dataRowIndex, column)}
        />
      );
    },
    [columnsWithWidths, data, onCellClick]
  );

  // Render grid
  const renderGrid = () => {
    // If error, show error state
    if (error) {
      return renderError();
    }
    
    // If no data, show empty state
    if (!data || !data.rowCount) {
      return renderEmpty();
    }
    
    // Get row range for current page
    const { startRow, endRow } = getRowRange();
    const rowCount = endRow - startRow;
    
    // Calculate total width of all columns
    const totalColumnsWidth = columnsWithWidths.reduce((total, column) => {
      // Use column width or a default minimum width
      const columnWidth = parseInt(String(column.width || column.minWidth || 100), 10);
      return total + columnWidth;
    }, 0);

    // Determine if we need horizontal scrolling - only if total width is greater than container width
    // and width is a specific pixel value (not a percentage)
    const needsHorizontalScroll = typeof width === 'number' && totalColumnsWidth > width;
    
    // Set table width based on whether we need horizontal scrolling
    const tableWidth = needsHorizontalScroll 
      ? `${totalColumnsWidth}px` 
      : '100%';

    return (
      <div className="w-full h-full flex flex-col">
        {/* Main scrollable container that wraps both header and body */}
        <div 
          className="flex-1 overflow-hidden w-full"
          ref={scrollContainerRef}
        >
          <ScrollArea className="w-full h-full">
            <div style={{ width: '100%' }}>
              {/* Table with header and body together */}
              <table style={{ 
                width: tableWidth,
                tableLayout: 'fixed',
                borderCollapse: 'collapse'
              }} className="w-full">
                {/* Table header with sticky positioning to keep it visible during scroll */}
                <thead className="bg-background z-10 border-b sticky top-0">
                  <tr>
                    {columnsWithWidths.map((column) => (
                      <th
                        key={`${column.name}-${sortInfo.field === column.name ? sortInfo.direction : 'none'}`}
                        id={column.name}
                        className={cn(
                          "px-3 py-1.5 text-left border-b font-medium text-sm relative",
                          "whitespace-nowrap overflow-hidden text-ellipsis",
                          column.sortable && query?.type !== 'SQL' && "cursor-pointer select-none",
                          sortInfo.field === column.name && "bg-primary/10 border-primary border-b-2 text-primary"
                        )}
                        style={{
                          width: `${getColumnWidth(column)}px`,
                          maxWidth: `${getColumnWidth(column)}px`,
                          minWidth: column.minWidth ? `${column.minWidth}px` : '40px',
                        }}
                        onClick={() => column.sortable && query?.type !== 'SQL' && handleSortChange(column.name)}
                      >
                        <div className="flex items-center gap-1 relative w-full">
                          <span className="truncate">{column.header || column.name}</span>
                          {column.sortable && query?.type !== 'SQL' && (
                            <div className="ml-1 flex items-center flex-shrink-0">
                              {sortInfo.field === column.name ? (
                                <div className="text-primary">
                                  {sortInfo.direction === 'asc' ? (
                                    <ArrowUp className="h-3 w-3" strokeWidth={2} />
                                  ) : (
                                    <ArrowDown className="h-3 w-3" strokeWidth={2} />
                                  )}
                                </div>
                              ) : (
                                <ArrowUpDown className="h-2.5 w-2.5 text-muted-foreground opacity-50" />
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Column resize handle */}
                        {column.resizable !== false && (
                          <div
                            key={`resize-${column.name}`}
                            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize group hover:bg-muted/30"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleColumnResizeStart(column.name, getColumnWidth(column), e.clientX);
                            }}
                          >
                            <div className="h-4/5 w-[1px] mx-auto my-auto bg-primary/50 group-hover:bg-primary" />
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: rowCount }).map((_, rowIndex) => {
                    const dataRowIndex = startRow + rowIndex;
                    return (
                      <MemoizedRow
                        key={dataRowIndex}
                        rowIndex={dataRowIndex}
                        columns={columnsWithWidths}
                        data={data}
                        onRowClick={onRowClick}
                        onCellClick={onCellClick}
                      />
                    );
                  })}
                </tbody>
              </table>
              <ScrollBar orientation="horizontal" />
            </div>
          </ScrollArea>
        </div>
        
        {/* Pagination controls */}
        <div className="flex-shrink-0 w-full">
          {renderPaginationButtons()}
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div className={cn("w-full h-full relative", className)} style={{ width: '100%', position: 'relative' }}>
      {/* Wrap the grid in a stable container that doesn't remount */}
      <div className="w-full h-full">
        {renderGrid()}
      </div>
      {/* Position the loading overlay absolutely over the content */}
      {loading && showLoadingIndicator && renderLoading()}
    </div>
  );
} 